import React, { useState, useEffect } from 'react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Input,
  IconButton,
  RadioGroup,
  RadioGroupItem,
  Icon
} from '@shopflo/ui'
import { CaretDown, Check } from '@phosphor-icons/react'
import { classNames } from 'src/lib/utils/helpers'

import FormErrorText from 'src/components/common/FormErrorText'

interface UsageLimitPopoverProps {
  value: number | null
  onChange: (value: number | null) => void
  label: string
  triggerPrefix: string
  icon: any
  error?: string
}

type UsageLimitMode = 'UNLIMITED' | 'SET_LIMIT'

const UsageLimitPopover = ({ value, onChange, label, triggerPrefix, icon, error }: UsageLimitPopoverProps) => {
  const [open, setOpen] = useState(false)
  const [tempValue, setTempValue] = useState<number | null>(1)
  const [mode, setMode] = useState<UsageLimitMode>('UNLIMITED')

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (isOpen) {
      setMode(value === null ? 'UNLIMITED' : 'SET_LIMIT')
      setTempValue(value ?? 1)
    }
  }

  const handleApply = () => {
    if (mode === 'UNLIMITED') {
      onChange(null)
    } else {
      onChange(tempValue || 1)
    }
    setOpen(false)
  }

  const handleModeChange = (newMode: UsageLimitMode) => {
    setMode(newMode)
    if (newMode === 'UNLIMITED') {
      onChange(null)
      setOpen(false)
    }
  }

  return (
    <div className='flex flex-col w-full px-3 hover:bg-zinc-500/5 rounded-xl'>
      <div className='flex flex-col'>
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              id={`${label}-trigger`}
              className='flex items-center justify-start w-full gap-1.5 py-3 border-none bg-transparent p-0 shadow-none hover:bg-transparent cursor-pointer'
            >
              <Icon IconComponent={icon} size='sm' className='text-zinc-400' />
              <span className='text-text-sm-regular text-zinc-500'>{triggerPrefix} </span>
              <div
                className={`flex items-center gap-1 text-text-sm-semibold transition-colors hover:text-zinc-900 ${
                  error ? 'text-red-500' : 'text-zinc-600'
                }`}
              >
                <span>{value === null ? 'unlimited times' : `${value} time(s)`}</span>
                <Icon IconComponent={CaretDown} size={14} className='text-zinc-400' />
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent
            align='start'
            className='w-[260px] p-1.5 bg-white !border-none !rounded-xl overflow-hidden'
            sideOffset={8}
          >
            <div className='flex flex-col gap-1'>
              <RadioGroup
                value={mode}
                onValueChange={val => handleModeChange(val as UsageLimitMode)}
                className='flex flex-col gap-1'
              >
                <div
                  id={`${label}-unlimited-option`}
                  className={classNames(
                    'flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors',
                    mode === 'UNLIMITED' ? '!bg-indigo-500/8' : 'hover:bg-zinc-50'
                  )}
                  onClick={() => handleModeChange('UNLIMITED')}
                >
                  <RadioGroupItem
                    value='UNLIMITED'
                    id={`${label}-unlimited`}
                    label='Unlimited times'
                    labelClassName='cursor-pointer text-text-sm-regular text-zinc-700'
                  />
                </div>
                <div
                  id={`${label}-limit-option`}
                  className={classNames(
                    'flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors',
                    mode === 'SET_LIMIT' ? '!bg-indigo-500/8' : 'hover:bg-zinc-50'
                  )}
                  onClick={() => setMode('SET_LIMIT')}
                >
                  <RadioGroupItem
                    value='SET_LIMIT'
                    id={`${label}-limit`}
                    label='Set limit'
                    labelClassName='cursor-pointer text-text-sm-regular text-zinc-700'
                  />
                </div>
              </RadioGroup>

              {mode === 'SET_LIMIT' && (
                <div className='flex items-center gap-1.5 pt-1 animate-in fade-in slide-in-from-top-1'>
                  <div className='relative flex-1'>
                    <Input
                      id={`${label}-input`}
                      type='number'
                      value={tempValue ?? ''}
                      onChange={e => setTempValue(e.target.value ? parseInt(e.target.value) : null)}
                      className='pr-16 text-sm h-9'
                      dimension='md'
                      min={1}
                    />
                    <div className='absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-400 pointer-events-none'>
                      time(s)
                    </div>
                  </div>
                  <IconButton
                    id={`${label}-apply-button`}
                    IconComponent={Check}
                    variant='outline'
                    size='sm'
                    onClick={handleApply}
                    className='h-9 w-9 border-zinc-200 text-zinc-600 hover:bg-zinc-50 rounded-md'
                  />
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <FormErrorText message={error} />
    </div>
  )
}

export default UsageLimitPopover
